import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  LockReset as ResetIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import {
  User,
  UserFilters,
  UserFormData,
  USER_ROLES,
  PAGE_SIZE_OPTIONS,
} from '../types/user';

export const UserManagementPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<UserFilters>({});

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    role: 'user',
    can_delete_cpe: false,
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userService.getUsers(page + 1, rowsPerPage, filters);
      setUsers(response.data.users);
      setTotal(response.data.pagination.total);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = () => {
    setFilters({ ...filters, search: searchTerm || undefined });
    setPage(0);
  };

  const handleFilterChange = (key: keyof UserFilters, value: string | 'user' | 'admin' | 'super_admin' | boolean | undefined) => {
    setFilters({ ...filters, [key]: value || undefined });
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateUser = async () => {
    try {
      setError('');
      const result = await userService.createUser(formData);
      setSuccess(`User created successfully! Temporary password: ${result.temporaryPassword}`);
      setCreateDialogOpen(false);
      setFormData({ email: '', name: '', role: 'user', can_delete_cpe: false });
      loadUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      setError('');
      await userService.updateUser(selectedUser.id, formData);
      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      setError('');
      await userService.deleteUser(selectedUser.id);
      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleRestoreUser = async (user: User) => {
    try {
      setError('');
      await userService.restoreUser(user.id);
      setSuccess('User restored successfully');
      loadUsers();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to restore user');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      setError('');
      const tempPassword = await userService.resetUserPassword(selectedUser.id);
      setSuccess(`Password reset successfully! Temporary password: ${tempPassword}`);
      setResetPasswordDialogOpen(false);
      setSelectedUser(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      can_delete_cpe: user.can_delete_cpe,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const openResetPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setResetPasswordDialogOpen(true);
  };

  if (currentUser?.role !== 'super_admin') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{t('common.noPermission')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            {t('users.title')}
          </Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              sx={{ mr: 2 }}
            >
              {t('cpe.backToDashboard')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              {t('users.addUser')}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('filters.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label={t('users.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 250 }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('users.role')}</InputLabel>
              <Select
                value={filters.role || ''}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                label={t('users.role')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {USER_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {t(`users.roles.${role}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setFilters({});
                setPage(0);
              }}
            >
              {t('filters.clearFilters')}
            </Button>
          </Box>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('common.email')}</TableCell>
                <TableCell>{t('users.role')}</TableCell>
                <TableCell>{t('users.canDelete')}</TableCell>
                <TableCell>{t('users.canDownloadReports')}</TableCell>
                <TableCell>{t('users.status')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t('common.noDataFound')}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{ opacity: user.deleted_at ? 0.5 : 1 }}
                  >
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(`users.roles.${user.role}`)}
                        color={
                          user.role === 'super_admin'
                            ? 'error'
                            : user.role === 'admin'
                            ? 'warning'
                            : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.role === 'admin' && (
                        <Chip
                          label={user.can_delete_cpe ? t('common.yes') : t('common.no')}
                          color={user.can_delete_cpe ? 'success' : 'default'}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {(user.role === 'admin' || user.role === 'super_admin') && (
                        <Chip
                          label={user.can_download_reports ? t('common.yes') : t('common.no')}
                          color={user.can_download_reports ? 'success' : 'default'}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {user.deleted_at ? (
                        <Chip label={t('users.deleted')} color="error" size="small" />
                      ) : user.must_change_password ? (
                        <Chip label={t('common.mustChangePassword')} color="warning" size="small" />
                      ) : (
                        <Chip label={t('users.active')} color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {!user.deleted_at ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(user)}
                            disabled={user.id === currentUser?.id}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openResetPasswordDialog(user)}
                          >
                            <ResetIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(user)}
                            disabled={user.id === currentUser?.id}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleRestoreUser(user)}
                        >
                          <RestoreIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.createNewUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('common.email')}
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('common.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>{t('users.role')}</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'admin' | 'super_admin' })}
                label={t('users.role')}
              >
                {USER_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {t(`users.roles.${role}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.role === 'admin' && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.can_delete_cpe}
                      onChange={(e) => setFormData({ ...formData, can_delete_cpe: e.target.checked })}
                    />
                  }
                  label={t('users.canDelete')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.can_download_reports}
                      onChange={(e) => setFormData({ ...formData, can_download_reports: e.target.checked })}
                    />
                  }
                  label={t('users.canDownloadReports')}
                />
              </>
            )}
            {formData.role === 'super_admin' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.can_download_reports}
                    onChange={(e) => setFormData({ ...formData, can_download_reports: e.target.checked })}
                  />
                }
                label={t('users.canDownloadReports')}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreateUser} variant="contained">
            {t('users.createUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.editUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('common.email')}
              type="email"
              value={formData.email}
              disabled
              fullWidth
            />
            <TextField
              label={t('common.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>{t('users.role')}</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'admin' | 'super_admin' })}
                label={t('users.role')}
              >
                {USER_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {t(`users.roles.${role}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.role === 'admin' && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.can_delete_cpe}
                      onChange={(e) => setFormData({ ...formData, can_delete_cpe: e.target.checked })}
                    />
                  }
                  label={t('users.canDelete')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.can_download_reports}
                      onChange={(e) => setFormData({ ...formData, can_download_reports: e.target.checked })}
                    />
                  }
                  label={t('users.canDownloadReports')}
                />
              </>
            )}
            {formData.role === 'super_admin' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.can_download_reports}
                    onChange={(e) => setFormData({ ...formData, can_download_reports: e.target.checked })}
                  />
                }
                label={t('users.canDownloadReports')}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            {t('users.updateUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('users.deleteUser')}</DialogTitle>
        <DialogContent>
          {t('users.deleteConfirmMessage', 'Are you sure you want to delete user')} <strong>{selectedUser?.name}</strong>? {t('users.deleteReversible', 'This action can be reversed later.')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)}>
        <DialogTitle>{t('users.resetPassword')}</DialogTitle>
        <DialogContent>
          {t('users.resetPasswordConfirm', 'Are you sure you want to reset the password for')} <strong>{selectedUser?.name}</strong>? {t('users.resetPasswordInfo', 'A new temporary password will be generated.')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleResetPassword} color="warning" variant="contained">
            {t('users.resetPassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

